import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { queryKeys } from 'src/easyclaim.app.libs/queryKeys';
import { isAbortError, reportError } from 'src/easyclaim.app.libs/reportError';
import { fetchLogo } from 'src/easyclaim.app.services/LogoService';
import { useLogoFromCache, useLogoStore } from 'src/easyclaim.app.stores/useLogoStore';

import { useIsPerformanceEnhancerEnabled } from './useIsPerformanceEnhancerEnabled';

interface UseLogoOptions {
	caseId: string | undefined;
	enabled?: boolean;
}

/**
 * Fetches (and caches, via {@link useLogoStore}) the customer logo for a case, avoiding
 * a re-fetch every time `LogoImage` remounts (e.g. on every header/nav re-render).
 * Gated by the `canUseExperimentalPerformanceEnhancer` customer flag.
 */
export function useLogo({ caseId, enabled = true }: UseLogoOptions) {
	const cachedLogo = useLogoFromCache(caseId);
	const setLogo = useLogoStore((state) => state.setLogo);
	const isEnabled = useIsPerformanceEnhancerEnabled();

	const query = useQuery({
		queryKey: queryKeys.logo.detail(caseId ?? ''),
		queryFn: ({ signal }) => fetchLogo({ caseId: caseId!, signal }),
		enabled: isEnabled && enabled && !!caseId && !cachedLogo,
		staleTime: Infinity,
	});

	useEffect(() => {
		if (query.data && caseId) {
			setLogo(caseId, query.data.dataUrl);
		}
	}, [query.data, caseId, setLogo]);

	useEffect(() => {
		if (isEnabled && query.isError && !isAbortError(query.error)) {
			reportError(query.error, { context: 'useLogo', caseId });
		}
	}, [isEnabled, query.isError, query.error, caseId]);

	return {
		...query,
		isEnabled,
		logo: isEnabled ? (cachedLogo ?? query.data?.dataUrl) : undefined,
	};
}
